import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useDeleteSession, useDeleteSessionSeries } from '@/hooks/use-director-data';

interface DeleteSessionDialogProps {
  sessionId: string;
  seriesId?: string | null;
  onDeleted: () => void;
}

type DeleteMode = 'single' | 'series' | null;

export function DeleteSessionDialog({
  sessionId,
  seriesId,
  onDeleted,
}: DeleteSessionDialogProps) {
  const [open, setOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState<DeleteMode>(null);
  const [error, setError] = useState<string | null>(null);

  const deleteSession = useDeleteSession();
  const deleteSessionSeries = useDeleteSessionSeries();

  const isPending = deleteSession.isPending || deleteSessionSeries.isPending;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setDeleteMode(null);
      setError(null);
    }
  }

  async function handleConfirm() {
    setError(null);
    try {
      if (deleteMode === 'series' && seriesId) {
        await deleteSessionSeries.mutateAsync(sessionId, seriesId);
      } else {
        await deleteSession.mutateAsync(sessionId);
      }
      setOpen(false);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  const isSeries = !!seriesId;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Delete
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent>
        {deleteMode === 'series' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete This and Future Sessions?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this session and all future sessions in the
                series, along with their attendance records, evaluations, and notes.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteMode(null)} disabled={isPending}>
                Go Back
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={isPending}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {isPending ? 'Deleting…' : 'Confirm Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Session</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this session and all associated records
                (attendance, evaluations, notes). This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <AlertDialogFooter className={isSeries ? 'flex-col sm:flex-row gap-2' : ''}>
              <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
              {isSeries ? (
                <>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setError(null);
                      try {
                        await deleteSession.mutateAsync(sessionId);
                        setOpen(false);
                        onDeleted();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
                      }
                    }}
                    disabled={isPending}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    {deleteSession.isPending ? 'Deleting…' : 'Delete This Session Only'}
                  </Button>
                  <Button
                    onClick={() => setDeleteMode('series')}
                    disabled={isPending}
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                  >
                    Delete This & Future Sessions
                  </Button>
                </>
              ) : (
                <AlertDialogAction
                  onClick={handleConfirm}
                  disabled={isPending}
                  className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                >
                  {isPending ? 'Deleting…' : 'Delete Session'}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
